import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-balham.css';

import type { ColDef, GridOptions, ICellRendererParams, IsFullWidthRowParams } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import { useCallback, useEffect, useMemo } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';
import { useIntlContext } from '../../../hooks/useIntlContext';
import { createShouldForwardStyledProp } from '../../../styles';
import { InitialFetchStateProvider } from '../../AgGrid/AgGridLoadingOverlay';
import { BOTTOM_SCROLL_GRADIENT_HEIGHT } from '../../Mobile';
import { MOBILE_HEADER_HEIGHT } from '../../Mobile/MobileHeader/styles';
import { useMobileModal } from '../../Mobile/MobileModalSheet/MobileModalProvider';
import { DEFAULT_BLOTTER_COLDEF } from '../BlotterTable';
import { BlotterTableFormattedNumberProvider } from '../BlotterTableFormattedNumberProvider';
import { agOverlayEmptyCenterText } from '../styles/styles';
import { useBlotterComponents } from '../useBlotterComponents';
import { BlotterErrorDetail } from '../useBlotterTable/ErrorDetail';
import { useRenderCell } from '../useRenderCell';
import { getAgGridLocaleText } from '../utils';
import { MobileGroupCard } from './MobileGroupCard';
import { MobileRowDetailSheet } from './MobileRowDetailSheet';
import {
  MOBILE_CARD_HEIGHT,
  MOBILE_GROUP_CARD_HEIGHT,
  resolveMobileGroupCardHeight,
  type MobileCardRendererProps,
  type MobileDataTableProps,
  type MobileGroupCardRendererProps,
  type MobileRowPressParams,
} from './types';

const messages = defineMessages({
  details: {
    defaultMessage: 'Details',
    id: 'BlotterTable.Mobile.details',
  },
});

/**
 * Mobile-friendly counterpart to {@link BlotterTable}. Reuses the entire
 * blotter pipeline (data observable, transactions, filters, sorting, group
 * expansion, persistence) but renders each leaf row as a full-width card via
 * a consumer-supplied `cardRenderer`.
 *
 * Cell renderers are not re-implemented for mobile: the consumer's card calls
 * `renderCell(colId)` to mount the same renderer the desktop grid uses.
 *
 * By default, tapping a row opens a {@link MobileRowDetailSheet} via the
 * nearest {@link MobileModalProvider}. Pass `onRowPress` to override this.
 */
export function MobileDataTable<TRow>({
  cardRenderer,
  cardRendererProps,
  groupCardRenderer,
  extraComponents,
  cardHeight = MOBILE_CARD_HEIGHT,
  groupCardHeight = MOBILE_GROUP_CARD_HEIGHT,
  detailsTitle,
  onRowPress,
  rowDetailHeader,
  rowDetailFooter,
  // Add extra margin to compensate for the mobile tab bar and some ag-grid sizing behaviour
  bottomPadding = BOTTOM_SCROLL_GRADIENT_HEIGHT * 3,
  topPadding = MOBILE_HEADER_HEIGHT,
  gridOptions,
  initialFetchState,
  settings,
  errorDetails,
}: MobileDataTableProps<TRow>) {
  const { formatMessage } = useIntl();
  const { locale } = useIntlContext();
  const components = useBlotterComponents(extraComponents);
  const { openModal } = useMobileModal();

  const defaultOnRowPress = useCallback(
    (params: MobileRowPressParams<TRow>) => {
      openModal({
        useSheetContent: true,
        title: detailsTitle ?? formatMessage(messages.details),
        content: (
          <MobileRowDetailSheet
            api={params.api}
            node={params.node}
            renderCell={params.renderCell}
            headerRenderer={rowDetailHeader}
            footerRenderer={rowDetailFooter}
          />
        ),
      });
    },
    [detailsTitle, formatMessage, openModal, rowDetailFooter, rowDetailHeader]
  );

  const resolvedOnRowPress = onRowPress ?? defaultOnRowPress;

  const isFullWidthRow = useCallback((params: IsFullWidthRowParams<TRow>) => !params.rowNode.group, []);

  const fullWidthCellRendererParams = useMemo(
    () => ({ cardRenderer, cardRendererProps, components, onRowPress: resolvedOnRowPress }),
    [cardRenderer, cardRendererProps, components, resolvedOnRowPress]
  );

  const groupRowRendererParams = useMemo(() => ({ groupCardRenderer, components }), [groupCardRenderer, components]);

  const resolvedGroupCardHeight = resolveMobileGroupCardHeight(cardHeight, groupCardHeight);
  const getRowHeight = useCallback(
    ({ node }: { node: { group?: boolean } }) => (node.group ? resolvedGroupCardHeight : cardHeight),
    [cardHeight, resolvedGroupCardHeight]
  );

  const mobileGridOptions = useMemo(() => {
    const merged: GridOptions<TRow> = {
      ...gridOptions,
      isFullWidthRow,
      fullWidthCellRenderer: MobileRowCardWrapper,
      fullWidthCellRendererParams,
      groupRowRenderer: groupCardRenderer ? MobileGroupRowWrapper : MobileGroupCard,
      groupRowRendererParams,
      groupDisplayType: 'groupRows',
      rowHeight: cardHeight,
      getRowHeight,
      headerHeight: 0,
      suppressHorizontalScroll: true,
      animateRows: false,
    };
    return merged;
  }, [
    gridOptions,
    isFullWidthRow,
    fullWidthCellRendererParams,
    groupRowRendererParams,
    groupCardRenderer,
    cardHeight,
    getRowHeight,
  ]);

  return (
    <InitialFetchStateProvider value={initialFetchState}>
      <BlotterTableFormattedNumberProvider settings={settings}>
        <MobileDataTableWrapper
          className="ag-theme-balham-dark"
          data-testid="mobile-data-table"
          topPadding={topPadding}
          bottomPadding={bottomPadding}
        >
          <BlotterErrorDetail
            errorDetails={errorDetails}
            disableErrorBanner={gridOptions?.context?.current?.settings?.disableErrorBanner}
          />
          <AgGridReact
            localeText={getAgGridLocaleText(locale)}
            defaultColDef={DEFAULT_BLOTTER_COLDEF as ColDef<TRow>}
            {...(extraComponents?.noRowsOverlay != null ? { noRowsOverlayComponent: 'noRowsOverlay' } : {})}
            groupAllowUnbalanced={true}
            {...mobileGridOptions}
            components={components}
            loadingOverlayComponent="loadingOverlay"
          />
        </MobileDataTableWrapper>
      </BlotterTableFormattedNumberProvider>
    </InitialFetchStateProvider>
  );
}

interface MobileRowCardWrapperParams<TRow> extends ICellRendererParams<TRow> {
  cardRenderer: React.ComponentType<MobileCardRendererProps<TRow>>;
  cardRendererProps?: Omit<Partial<MobileCardRendererProps<TRow>>, 'data' | 'node' | 'api' | 'renderCell' | 'onPress'>;
  components: Record<string, any>;
  onRowPress: (params: MobileRowPressParams<TRow>) => void;
}

function MobileRowCardWrapper<TRow>({
  node,
  api,
  cardRenderer: CardRenderer,
  cardRendererProps,
  components,
  onRowPress,
}: MobileRowCardWrapperParams<TRow>) {
  // node and node.data are already correctly typed via MobileRowCardWrapperParams<TRow>
  // which extends ICellRendererParams<TRow>: node is IRowNode<TRow>, data is TRow | undefined.
  const renderCell = useRenderCell<TRow>(api, node, components);

  if (node.data == null) {
    return null;
  }

  // After the null guard above, TypeScript narrows node.data to TRow.
  const data = node.data;

  const handlePress = () => {
    onRowPress({ data, node, api, renderCell });
  };

  return (
    <CardRenderer
      data={data}
      node={node}
      api={api}
      renderCell={renderCell}
      onPress={handlePress}
      {...cardRendererProps}
    />
  );
}

interface MobileGroupRowWrapperParams<TRow> extends ICellRendererParams<TRow> {
  groupCardRenderer: React.ComponentType<MobileGroupCardRendererProps<TRow>>;
  components: Record<string, any>;
}

function MobileGroupRowWrapper<TRow>({
  groupCardRenderer: GroupCardRenderer,
  components,
  ...cellParams
}: MobileGroupRowWrapperParams<TRow>) {
  // AG Grid does not automatically refresh custom group row renderers when an aggregated cell value changes.
  useEffect(() => {
    const refreshGroupRow = () => {
      cellParams.api.refreshCells({ rowNodes: [cellParams.node], force: true });
    };
    cellParams.node.addEventListener('cellChanged', refreshGroupRow);
    return () => cellParams.node.removeEventListener('cellChanged', refreshGroupRow);
  }, [cellParams.api, cellParams.node]);

  const renderCell = useRenderCell<TRow>(cellParams.api, cellParams.node, components);
  return <GroupCardRenderer {...cellParams} renderCell={renderCell} />;
}

const EXTRA_SPACING = 8;

export const MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY = '--mobile-data-table-top-padding';

const MOBILE_DATA_TABLE_WRAPPER_PROPS = new Set(['topPadding', 'bottomPadding']);
type MobileDataTableWrapperProps = {
  bottomPadding: number;
  topPadding: number;
};

const MobileDataTableWrapper = styled.div
  .withConfig({
    shouldForwardProp: createShouldForwardStyledProp(MOBILE_DATA_TABLE_WRAPPER_PROPS),
  })
  .attrs<MobileDataTableWrapperProps>(({ bottomPadding, style }) => ({
    style: {
      ...style,
      '--mobile-data-table-bottom-padding': `${bottomPadding}px`,
    },
  }))<MobileDataTableWrapperProps>`
  width: 100%;
  height: 100%;
  background: var(--backgroundContent);
  ${MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY}: ${({ topPadding }) => topPadding + EXTRA_SPACING}px;

  --ag-background-color: var(--backgroundContent);
  --ag-modal-overlay-background-color: var(--backgroundContent);
  --ag-borders: none;
  --ag-font-size: ${({ theme }) => theme.fontSizeDefault}rem;
  --ag-row-hover-color: var(--backgroundContent);

  /* Hide the empty header row entirely. */
  .ag-header {
    display: none !important;
  }

  /* Remove cell borders so cards control their own visual spacing. */
  .ag-cell,
  .ag-full-width-row .ag-cell-wrapper {
    padding: 0;
    border: none;
  }

  .ag-overlay {
    z-index: 0;
  }

  ${({ theme }) => agOverlayEmptyCenterText(theme)}

  /* Add top margin to the full-width container so the first row is visible when scrolling to the top. */
  .ag-full-width-container {
    margin-top: calc(var(${MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY}) + env(safe-area-inset-top));
    min-height: var(${MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY});
  }

  /* Add bottom padding to the viewport so bottom rows are visible when scrolling to the bottom. */
  .ag-center-cols-viewport {
    margin-bottom: calc(
      var(--mobile-data-table-bottom-padding) + env(safe-area-inset-bottom) + env(safe-area-inset-top)
    );
    min-height: calc(
      100% - var(--mobile-data-table-bottom-padding) - env(safe-area-inset-bottom) - env(safe-area-inset-top)
    );
  }

  /*
   * Expanded group rows stick to the top while scrolling. Offset the sticky
   * container below the mobile header and paint a backdrop above it so leaf
   * cards do not show through the safe-area gap.
   */
  .ag-sticky-top {
    overflow: visible;

    &:before {
      content: '';
      position: absolute;
      top: -1px;
      left: 0;
      width: 100%;
      transform: translateY(-100%);
      height: var(${MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY});
      background: transparent;
      transition: background 300ms linear;
    }

    &:not([style*='height: 0px']) {
      margin-top: calc(var(${MOBILE_DATA_TABLE_TOP_PADDING_PROPERTY}) + env(safe-area-inset-top));
      border-top: 1px solid var(--backgroundDivider);

      &:before {
        background: var(--backgroundContent);
      }
    }
  }

  .ag-row {
    border: none;
    background: transparent;
  }

  .ag-full-width-row {
    background: var(--backgroundContent);
    border: none;
  }

  .ag-row-level-1 {
    background: ${({ theme }) => theme.colors.gray['020']};
  }

  .ag-row-level-2 {
    background: ${({ theme }) => theme.colors.gray['030']};
  }

  .ag-row-level-3 {
    background: ${({ theme }) => theme.colors.gray['040']};
  }

  .ag-row-level-4 {
    background: ${({ theme }) => theme.colors.gray['050']};
  }
`;
